import type { CustomVars } from '@/components/ui/TextareaWithVars/interfaces'
import { normalizeMerchantRewardSavePayload } from '@/components/rewards/utils/rewardHelpers'

import type { ContestMechanic } from '@/types/global'
import type { ContestGameReward, LoyaltyReward } from '@/types/types'
import type { RewardTypeSlug } from '@/views/contests/Components/types/global'

export const CONTEST_MECHANICS: ContestMechanic[] = ['fortuneWheel', 'scratchCard', 'mysteryBox']

export const MAX_CONTEST_REWARDS = 8

export const isContestMechanic = (value: unknown): value is ContestMechanic =>
  typeof value === 'string' && CONTEST_MECHANICS.includes(value as ContestMechanic)

/**
 * Creates a new reward with default values
 */
export const createNewReward = ({
  type,
  index,
  colors,
  t
}: {
  type: string
  index: number
  colors: { color: string; textColor: string }
  t?: (key: string) => string
}): ContestGameReward => {
  const rewardTypeMap: Record<string, RewardTypeSlug> = {
    gift: 'free_product',
    points: 'points',
    lost: 'lost'
  }

  const getDefaultName = () => {
    if (!t) return type === 'points' ? 'Points Bonus' : type === 'lost' ? 'Lost' : 'Reward'
    return type === 'points'
      ? t('pages.contests.buttons.bonus')
      : type === 'lost'
        ? t('pages.contests.buttons.lost')
        : t('pages.contests.buttons.reward')
  }

  const getColor = () => {
    const color = index % 2 === 1 ? colors.color : colors.textColor
    const textColor = index % 2 === 1 ? colors.textColor : colors.color
    return { color, textColor }
  }

  const finalColors = getColor()

  return {
    name: getDefaultName(),
    color: type === 'lost' ? '#ef4444' : finalColors.color,
    textColor: type === 'lost' ? '#ffffff' : finalColors.textColor,
    notificationTextOverride: '',
    points: type === 'points' ? 10 : 0,
    probability: 10,
    rewardTypeSlug: rewardTypeMap[type] || 'free_product',
    minimumOrderAmount: null,
    externalId: null,
    value: null,
    validityInDays: null,
    rewardTypeId: null,
    rewardId: null
  }
}

/**
 * Validates probability value
 */
export const validateProbability = (probability: number): boolean => {
  return (
    probability > 0 && probability <= 100 && !isNaN(probability) && probability !== null && probability !== undefined
  )
}

/**
 * Calculates total probability from rewards
 */
export const calculateTotalProbability = (rewards: { probability: number }[]): number => {
  return Number(rewards.reduce((total, reward) => total + (reward.probability || 0), 0).toFixed(4))
}

export const withEveryDaySlotProbability = <T extends ContestGameReward>(reward: T): T => ({
  ...reward,
  slots: reward.slots?.map((slot) =>
    slot.dayFromStart === null && slot.dayOfWeek === null ? { ...slot, probability: reward.probability } : slot
  )
})

/**
 * Calculates remaining probability
 */
export const calculateRemainingProbability = (rewards: ContestGameReward[]): number => {
  const total = calculateTotalProbability(rewards)
  return Math.max(0, 100 - total)
}

/**
 * Checks if probability has error (total > 1)
 */
export const hasProbabilityError = (rewards: ContestGameReward[]): boolean => {
  return calculateTotalProbability(rewards) > 100
}

/**
 * Formats probability for display (always shows as percentage)
 */
export const formatProbability = (probability: number): string => {
  return `${roundToDecimals(probability)}%`
}

/**
 * Updates reward probability with validation
 * Input is percentage (0-100), stored as float (0-1)
 */
export const updateRewardProbability = (reward: ContestGameReward, value: number): void => {
  reward.probability = roundToDecimals(value)
}

export const roundToDecimals = (probability: number | string): number => {
  return Number(Number(probability).toFixed(4))
}

export const getCustomVars = (
  editingReward: ContestGameReward,
  variablesLength: Record<string, number>,
  t?: (key: string) => string,
  hasCompatibleVoucherIntegration?: boolean
) => {
  const variables: CustomVars[] = [
    {
      type: 'variable',
      code: 'points',
      label: t('global.customVars.points.name'),
      icon: 'HeroStarIconSolid',
      disabledMessage: t('global.customVars.points.disabledMessage'),
      length: editingReward.points?.toString()?.length,
      example: editingReward.points?.toString()
    },
    {
      type: 'variable',
      code: 'reward',
      label: t('global.customVars.reward.name'),
      icon: 'HeroGiftIconSolid',
      disabledMessage: t('global.customVars.reward.disabledMessage'),
      length: editingReward.name?.length,
      example: editingReward.name,
      disabled: !editingReward.name
    },
    {
      code: 'customerApp',
      label: t ? t('global.customVars.customerApp.rewards.name') : null,
      icon: 'HeroGlobeAltIconSolid',
      type: 'webapp',
      length: variablesLength?.customerApp ?? 16,
      example: 'pngo.fr/webapp'
    },
    {
      code: 'customerApp(customer-profile)',
      label: t ? t('global.customVars.customerApp.customerProfile.name') : null,
      icon: 'HeroGlobeAltIconSolid',
      type: 'webapp',
      length: variablesLength?.customerApp ?? 16,
      example: 'pngo.fr/webapp'
    },
    {
      code: 'customerApp(wallet)',
      label: t ? t('global.customVars.customerApp.wallet.name') : null,
      icon: 'HeroGlobeAltIconSolid',
      type: 'webapp',
      length: variablesLength?.customerApp ?? 16,
      example: 'pngo.fr/webapp'
    },
    {
      type: 'variable',
      code: 'voucher',
      label: t('global.customVars.voucher.name'),
      icon: 'HeroReceiptPercentIconSolid',
      length: variablesLength?.voucher ?? 10,
      example: 'XXXXXXXXXX',
      disabled: !hasCompatibleVoucherIntegration,
      disabledMessage: t ? t('global.customVars.voucher.disabledMessageTargetStore') : null
    }
  ]

  if (editingReward.rewardTypeSlug !== 'points') {
    variables.push({
      type: 'webapp',
      code: 'rewardLink',
      label: t('global.customVars.rewardLink.name'),
      icon: 'HeroGiftIconOutline',
      length: variablesLength?.rewardLink ?? 16,
      example: 'pngo.fr/reward',
      disabled: !editingReward.name,
      disabledMessage: t('global.customVars.rewardLink.disabledMessage')
    })
  }

  return variables
}

export const getPointsDefaultNotificationText = (t: (key: string, params?: object) => string, isPolite: boolean) => {
  const value = isPolite ? 'valueFormal' : 'value'

  return t(`pages.settings.merchantForm.text.rewardWonTextsFields.pointsSmsNew.${value}`, {
    points: '{{ points }}',
    customerApp: '{{ customerApp }}'
  })
}

export const getRewardDefaultNotificationText = (t: (key: string, params?: object) => string, isPolite: boolean) => {
  const value = isPolite ? 'valueFormal' : 'value'

  return t(`pages.settings.merchantForm.text.rewardWonTextsFields.congratsSmsNew.${value}`, {
    reward: '{{ reward }}',
    rewardLink: '{{ rewardLink }}'
  })
}

export const createRewardFromLibrary = ({
  reward,
  index,
  colors,
  t,
  isPolite
}: {
  reward: LoyaltyReward
  index: number
  colors: { color: string; textColor: string }
  t: (key: string, params?: object) => string
  isPolite: boolean
}): ContestGameReward => ({
  ...createNewReward({ type: 'gift', index, colors, t }),
  name: reward.name,
  notificationTextOverride: getRewardDefaultNotificationText(t, isPolite),
  value: reward.value ?? null,
  minimumOrderAmount: reward.minimumOrderAmount ? Number(reward.minimumOrderAmount) : null,
  rewardTypeSlug: reward.rewardType.slug as RewardTypeSlug,
  rewardTypeId: reward.rewardTypeId ?? reward.rewardType.id,
  externalId: reward.externalId ?? null,
  rewardId: reward.id,
  pictureUrl: reward.pictureUrl
})

type RewardLike = Pick<ContestGameReward, 'rewardId'> & {
  rewardTypeSlug?: string
  rewardType?: { slug: string }
}

export const isRewardPlaceholder = (reward: RewardLike) =>
  (reward.rewardTypeSlug ?? reward.rewardType?.slug) === 'free_product' && !reward.rewardId

export const countRewardPlaceholders = (rewards: RewardLike[]) => rewards.filter(isRewardPlaceholder).length

export const mergeLibraryRewards = (
  rewards: ContestGameReward[],
  libraryRewards: ContestGameReward[]
): ContestGameReward[] => {
  const remaining = [...libraryRewards]

  const merged = rewards.map((reward) => {
    if (!isRewardPlaceholder(reward) || !remaining.length) return reward

    const libraryReward = remaining.shift()
    return {
      ...libraryReward,
      probability: reward.probability,
      color: reward.color,
      textColor: reward.textColor,
      points: reward.points,
      validityInDays: reward.validityInDays,
      notificationTextOverride: reward.notificationTextOverride || libraryReward.notificationTextOverride
    }
  })

  return [...merged, ...remaining]
}

export const distributeProbabilityEvenly = (rewards: ContestGameReward[]): ContestGameReward[] => {
  const lostProbability = calculateTotalProbability(rewards.filter((reward) => reward.rewardTypeSlug === 'lost'))
  const winningCount = rewards.filter((reward) => reward.rewardTypeSlug !== 'lost').length
  if (!winningCount) return rewards

  const available = roundToDecimals(100 - lostProbability)
  const share = Math.floor((available / winningCount) * 100) / 100
  let assigned = 0
  let position = 0

  return rewards.map((reward) => {
    if (reward.rewardTypeSlug === 'lost') return reward

    position++
    const probability = position === winningCount ? roundToDecimals(available - assigned) : share
    assigned = roundToDecimals(assigned + probability)

    return { ...reward, probability }
  })
}

export const buildContestRewardSavePayload = (reward: LoyaltyReward, isOfferEngineEnabled: boolean) => {
  if (!reward) return null

  const payload: any = {
    id: reward.id || null,
    rewardTypeId: reward.rewardType?.id || 1,
    name: reward.name,
    description: reward.description?.trim() || null,
    value: reward.value,
    externalId: reward.externalId,
    items: reward.items,
    source: reward.source || 'contest_game',

    voucherDiscountAmount: reward.voucherDiscountAmount || null,
    discountValuePercent: reward.discountValuePercent || null,
    minimumOrderAmount: reward.minimumOrderAmount
  }

  if (reward.minimumOrderAmount !== undefined) {
    payload.minimumOrderAmount = reward.minimumOrderAmount || 0
  }

  if (reward.isForSingleUse !== undefined) {
    payload.isForSingleUse = reward.isForSingleUse
  }

  if (reward.pictureUrl !== null) {
    payload.pictureUrl = reward.pictureUrl
  } else {
    delete payload.pictureUrl
  }

  if (isOfferEngineEnabled) {
    normalizeMerchantRewardSavePayload(payload, reward)
  }

  return payload
}

export const isUrlValid = (string: string) => {
  const regex = new RegExp("^https?://(?:www\\.)?[\\w\\-\\.~:/?#[\\]@!$&'()*+,;=%]{1,2000}$")
  return regex.test(string)
}

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}
